import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { SITE, whatsappLink } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor, preencha nome, email e mensagem.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada! Entraremos em contacto em breve.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Não foi possível enviar a mensagem. Tente novamente ou contacte-nos pelo WhatsApp.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="section bg-stone-50">
      <div className="container-tight">
        {/* CTA banner */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative rounded-[2rem] bg-bordeaux-700 overflow-hidden p-10 sm:p-14 lg:p-20 mb-20"
        >
          <div aria-hidden className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-bordeaux-500/40 blur-3xl" />
          <div aria-hidden className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-navy-700/40 blur-3xl" />
          <div className="relative max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-bordeaux-100 mb-4">Pronto para começar?</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              Ajudamos cada aluno a crescer<br /><span className="italic text-bordeaux-100">académica e pessoalmente.</span>
            </h2>
            <p className="mt-5 text-white/80 max-w-xl leading-relaxed">
              Marque uma conversa connosco — sem compromisso. Em poucos minutos, ajudamos a desenhar o melhor caminho.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-bordeaux-700 font-semibold hover:bg-bordeaux-50 transition-all hover:-translate-y-0.5"
              >
                <MessageCircle size={18} /> Falar no WhatsApp
              </a>
              <a
                href="#contact-form"
                data-testid="cta-form-btn"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-all"
              >
                Enviar mensagem
              </a>
            </div>
          </div>
        </motion.div>

        {/* Form + Info */}
        <div id="contact-form" className="grid lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3 bg-white rounded-3xl p-8 sm:p-10 border border-stone-100 shadow-[0_20px_50px_rgba(0,0,0,0.04)]"
          >
            <h3 className="font-display text-2xl sm:text-3xl text-stone-900">Envie-nos uma mensagem</h3>
            <p className="mt-2 text-stone-600 text-sm">Respondemos no próprio dia útil.</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nome *" name="name" value={form.name} onChange={onChange} testId="contact-input-name" required />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testId="contact-input-email" required />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Telefone" name="phone" value={form.phone} onChange={onChange} testId="contact-input-phone" />
                <Field label="Assunto" name="subject" value={form.subject} onChange={onChange} testId="contact-input-subject" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-stone-600 mb-2">Mensagem *</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-input-message"
                  className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-stone-900 outline-none focus:border-bordeaux-500 focus:ring-2 focus:ring-bordeaux-100 transition-all resize-none"
                  placeholder="Conte-nos um pouco sobre o aluno e o que procura..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-bordeaux-700 text-white font-semibold hover:bg-bordeaux-800 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_10px_30px_rgba(124,31,44,0.22)]"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "A enviar..." : "Enviar mensagem"}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-2 space-y-4"
          >
            <InfoCard icon={MessageCircle} title="WhatsApp" value={SITE.contact.whatsappDisplay} href={whatsappLink()} testId="contact-info-whatsapp" />
            <InfoCard icon={Mail} title="Email" value={SITE.contact.email} href={`mailto:${SITE.contact.email}`} testId="contact-info-email" />
            <InfoCard icon={MapPin} title="Morada" value={SITE.contact.address} testId="contact-info-address" />
            <div className="rounded-2xl bg-navy-700 text-white p-7" data-testid="contact-info-schedule">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Clock size={18} strokeWidth={1.5} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/70 font-semibold">Horários</div>
              </div>
              <ul className="mt-5 space-y-2.5 text-sm">
                {SITE.contact.schedule.map((s) => (
                  <li key={s.d} className="flex items-center justify-between gap-4 pb-2 border-b border-white/10 last:border-0">
                    <span className="text-white/80">{s.d}</span>
                    <span className="font-semibold text-white">{s.h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testId }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-stone-600 mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-stone-900 outline-none focus:border-bordeaux-500 focus:ring-2 focus:ring-bordeaux-100 transition-all"
    />
  </div>
);

const InfoCard = ({ icon: Icon, title, value, href, testId }) => {
  const inner = (
    <div className="rounded-2xl bg-white border border-stone-100 p-6 group hover:border-bordeaux-200 hover:-translate-y-0.5 transition-all hover:shadow-[0_15px_30px_rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-bordeaux-50 text-bordeaux-700 flex items-center justify-center group-hover:bg-bordeaux-700 group-hover:text-white transition-colors">
          <Icon size={18} strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold">{title}</div>
          <div className="text-stone-900 font-semibold mt-1 break-words">{value}</div>
        </div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testId} className="block">
      {inner}
    </a>
  ) : (
    <div data-testid={testId}>{inner}</div>
  );
};
